import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { Chart, ScatterController, LinearScale, PointElement, LineElement, Title, Tooltip } from "chart.js";
import { alpha } from "@mui/material/styles";
import { Selector } from "../../plugin/selector";
import { tokens, monoFont } from "../../theme";
import useChartPanZoom from "./useChartPanZoom";

// No Legend plugin - the kept/dropped legend lives once in the right rail's Fit Summary card
// instead (see RawSurfaceChart's own doc comment below), never chart.js's own.
Chart.register(ScatterController, LinearScale, PointElement, LineElement, Selector, Title, Tooltip);

/**
 * Fill opacity of the Machine/Motor range bands. Raised from 0.08 to 0.18 (Kevin, 2026-08-17) -
 * at 0.08 the red and green read as almost nothing against the dark chart background. It stays
 * well below the point marks so the bands remain a backdrop rather than competing with the data.
 */
const RANGE_BAND_ALPHA = 0.18;

// Fixed 0-320,000µm (320mm) per the mockup, matching the linear actuator's travel - also the
// domain useChartPanZoom's resetView() restores. TODO: some AUs use a 400mm actuator - derive
// this from the AU's own actuator length once that's wired up here, rather than hardcoding 320mm
// for all of them.
const X_BOUNDS = { min: 0, max: 320000 };

/**
 * Plots one point per fitted circle along the shaft, for a single metric (Centre Y, Centre Z,
 * or Radius - the three stacked per the mockup, one instance each, mounted side by side rather
 * than switched via a dropdown).
 *
 * Every circle is drawn - no decimation. A capture is ~2000 circles, which chart.js handles
 * comfortably, and decimating would hide exactly the isolated bad circles this view exists to
 * find. (The app's REACT_APP_POINT_THRESHOLD decimation is intentionally not reused here.)
 *
 * Kept and dropped circles are separate datasets so rejects stay visible rather than
 * disappearing: seeing which circles production discarded, and where along the shaft, is the
 * primary thing being diagnosed. The kept/dropped legend itself lives once in the right rail's
 * Fit Summary card rather than repeating on all three charts.
 */
const RawSurfaceChart = forwardRef(function RawSurfaceChart({
    circles, metric, onSelect, onRangeSelected, machineRange, motorRange, showRejected,
    title, unit = "microns", height = "160px", onXRangeChange,
}, ref) {
    const canvasRef = useRef(null);
    const chartRef = useRef(null);
    const tooltipRef = useRef(null);
    const containerRef = useRef(null);
    // Read at draw time (see the shaftRangeBands plugin below) rather than captured once at
    // chart-creation, since machineRange/motorRange change after the chart already exists.
    const rangesRef = useRef({ machineRange, motorRange });
    rangesRef.current = { machineRange, motorRange };

    // Pan/zoom by input (wheel/pinch/Space+drag), not a mode toggle - see useChartPanZoom's own
    // doc comment for why. resetView/setXRange are exposed to RawSurfaceContent.js via the
    // forwarded ref, so a shared "Reset view" control and cross-chart X-sync can reach in.
    const panZoom = useChartPanZoom({ xBounds: X_BOUNDS, onXRangeChange });
    useImperativeHandle(ref, () => ({
        resetView: panZoom.resetView,
        setXRange: panZoom.setXRange,
    }));
    // A plain click (no real drag) still fires the Selector plugin's mouseup handler, and a real
    // drag's mouseup is still followed by the browser's own synthetic click on the same canvas -
    // this pair of refs tells the two interactions apart so a drag doesn't also select whatever
    // circle happened to be under the cursor at release, and a plain click doesn't also open a
    // zero-width range popover.
    const draggedRef = useRef(false);

    const metricLabels = {
        centerY: "Centre Y (µm)",
        centerZ: "Centre Z (µm)",
        radius: "Radius (µm)",
        sigma: "Fit residual, sigma (µm)",
        centralAngle: "Central angle (°)",
        pointCount: "Valid points",
    };

    const axisLabel = { centerY: "Y", centerZ: "Z", radius: "R" };

    // Renders our own fixed-position div instead of Chart.js's canvas-drawn tooltip (enabled:
    // false + external below) - matches the mockup's hand-styled, three-line tooltip exactly
    // rather than the library's default chrome and this chart's much more verbose original
    // content (radius/sigma/angle/points/drop-reason all at once).
    const updateTooltip = (context) => {
        const el = tooltipRef.current;
        if (!el) return;
        const { tooltip } = context;
        const point = tooltip.dataPoints?.[0];
        const c = point?.raw?.circle;
        if (tooltip.opacity === 0 || !c) {
            el.style.opacity = "0";
            return;
        }

        const rejected = !c.kept || c.filterRejectReason;
        el.innerHTML = `X = <span style="color:rgba(255,255,255,0.75)">${c.x.toLocaleString()}</span> µm<br>`
            + `${axisLabel[metric] ?? ""} = <span style="color:rgba(255,255,255,0.75)">${c[metric].toFixed(0)}</span><br>`
            + (rejected
                ? `<span style="color:${tokens.reject}">rejected${c.kept ? ` (${c.filterRejectReason})` : ""}</span>`
                : `<span style="color:${tokens.kept}">kept</span>`);

        const canvasRect = canvasRef.current.getBoundingClientRect();
        el.style.left = `${canvasRect.left + tooltip.caretX}px`;
        el.style.top = `${canvasRect.top + tooltip.caretY - 10}px`;
        el.style.opacity = "1";
    };

    const DRAG_THRESHOLD_PX = 4;
    const onDataSelected = (start, end) => {
        if (Math.abs(end.position.x - start.position.x) < DRAG_THRESHOLD_PX) {
            return; // no real movement - a plain click, which onClick below handles instead
        }

        draggedRef.current = true;
        if (!onRangeSelected || !canvasRef.current) return;
        const rect = canvasRef.current.getBoundingClientRect();
        onRangeSelected({
            start: Math.min(start.data.x, end.data.x),
            stop: Math.max(start.data.x, end.data.x),
            anchorPosition: { top: rect.top + end.position.y, left: rect.left + end.position.x },
        });
    };

    useEffect(() => {
        if (!canvasRef.current) return;

        // Translucent Machine/Motor range bands, drawn behind the scatter points - matches the
        // mockup's band() drawing, just as a chart.js plugin instead of a hand-rolled canvas
        // draw loop. Registered per-instance (not Chart.register'd globally) since it needs this
        // component's own rangesRef closure.
        const shaftRangeBands = {
            id: "shaftRangeBands",
            beforeDatasetsDraw: (chart) => {
                const { chartArea, scales, ctx } = chart;
                if (!chartArea) return;
                const { machineRange: mr, motorRange: mtr } = rangesRef.current;

                const drawBand = (range, color) => {
                    if (!range) return;
                    const left = scales.x.getPixelForValue(range.start);
                    const right = scales.x.getPixelForValue(range.stop);
                    ctx.save();
                    ctx.fillStyle = color;
                    ctx.fillRect(left, chartArea.top, right - left, chartArea.height);
                    ctx.restore();
                };

                drawBand(mr, alpha(tokens.machine, RANGE_BAND_ALPHA));
                drawBand(mtr, alpha(tokens.motor, RANGE_BAND_ALPHA));
            },
        };

        chartRef.current = new Chart(canvasRef.current, {
            type: "scatter",
            plugins: [shaftRangeBands],
            data: {
                datasets: [
                    {
                        label: "Kept",
                        data: [],
                        backgroundColor: tokens.kept,
                        pointRadius: 2,
                    },
                    {
                        // Drawn slightly larger so a handful of rejects among thousands of kept
                        // circles stays findable rather than disappearing into the run.
                        label: "Dropped",
                        data: [],
                        backgroundColor: tokens.reject,
                        pointRadius: 3,
                    },
                ],
            },
            options: {
                animation: false,          // 2000 points; animating each filter change just adds lag
                parsing: false,            // data is already {x, y}
                normalized: true,
                maintainAspectRatio: false,
                interaction: { mode: "nearest", intersect: true },
                select: {
                    enabled: true,
                    onDataSelected,
                },
                scales: {
                    x: {
                        type: "linear",
                        min: X_BOUNDS.min,
                        max: X_BOUNDS.max,
                        title: { display: false },
                        grid: { color: tokens.line },
                        // Exactly 3 ticks (0 / 160,000 / 320,000), matching the mockup's
                        // simplified axis - a full auto-generated tick run is unnecessary
                        // clutter against a fixed, known domain.
                        ticks: {
                            color: tokens.muted,
                            font: { family: monoFont, size: 10 },
                            stepSize: 160000,
                        },
                    },
                    y: {
                        type: "linear",
                        title: { display: false },
                        grid: { color: tokens.line },
                        // The mockup keeps this axis to ~4 simplified ticks too - unlike x, the
                        // range here is per-metric/per-capture rather than a fixed domain, so a
                        // hard step size doesn't apply; maxTicksLimit is chart.js's equivalent.
                        ticks: { color: tokens.muted, font: { family: monoFont, size: 10 }, maxTicksLimit: 4 },
                    },
                },
                plugins: {
                    tooltip: {
                        enabled: false,
                        external: updateTooltip,
                    },
                },
                onClick: (evt, elements) => {
                    if (draggedRef.current) {
                        draggedRef.current = false;
                        return;
                    }
                    if (!onSelect || elements.length === 0) return;
                    const el = elements[0];
                    const point = chartRef.current.data.datasets[el.datasetIndex].data[el.index];
                    if (point?.circle) onSelect(point.circle);
                },
            },
        });

        // Synchronous, right after construction - see useChartPanZoom's doc comment for why this
        // must not be a reactive effect keyed on a ref value instead.
        const detachPanZoom = panZoom.attach(chartRef.current, containerRef.current);

        return () => {
            detachPanZoom();
            chartRef.current?.destroy();
            chartRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;

        const toPoint = (c) => ({ x: c.x, y: c[metric], circle: c });

        // "Rejected" now covers two independent reasons a circle doesn't count toward the shim:
        // circle-fix/angle/point/sigma gating (!c.kept, always known) and NoiseReductionScatter/
        // FocusRemoveWorst dropping it during the last shim calculation (c.filterRejectReason,
        // only known once that's been run - see RawSurfaceContent.js). Folded into one toggle/
        // dataset rather than a second one, since both answer the same question ("did this point
        // count towards the result").
        chart.data.datasets[0].data = circles
            .filter((c) => c.kept && !c.filterRejectReason && c.pointCount > 0)
            .map(toPoint);
        // Dropped circles are hidden by default (Kevin, 2026-08-10) - the toggle lives in the
        // Fit Summary card. Rejected/dropped counts and per-circle detail stay available in that
        // card and the drilldown regardless; this only affects the chart plot itself.
        chart.data.datasets[1].data = showRejected
            ? circles.filter((c) => (!c.kept || c.filterRejectReason) && c.pointCount > 0).map(toPoint)
            : [];
        chart.update("none");
    }, [circles, metric, showRejected]);

    // machineRange/motorRange live in rangesRef for the plugin's own reads, but the canvas still
    // needs an explicit redraw when they change - nothing else about the chart triggers one.
    useEffect(() => {
        chartRef.current?.update("none");
    }, [machineRange, motorRange]);

    return (
        <div
            ref={containerRef}
            style={{
                width: "100%",
                background: tokens.surface,
                border: `1px solid ${tokens.line}`,
                borderRadius: `${tokens.radius}px`,
                boxShadow: tokens.shadow,
                padding: "14px",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>{title ?? metricLabels[metric]}</Typography>
                <Typography variant="caption" color="text.secondary">{unit}</Typography>
            </Box>
            <div style={{ height, width: "100%" }}>
                <canvas ref={canvasRef} />
            </div>
            <div
                ref={tooltipRef}
                style={{
                    position: "fixed",
                    pointerEvents: "none",
                    background: tokens.ink,
                    color: "#fff",
                    fontSize: "11.5px",
                    borderRadius: "6px",
                    padding: "6px 9px",
                    lineHeight: 1.5,
                    boxShadow: tokens.shadow,
                    zIndex: 1500,
                    opacity: 0,
                    transform: "translate(-50%, -100%)",
                    whiteSpace: "nowrap",
                    transition: "opacity 0.08s ease",
                }}
            />
        </div>
    );
});

export default RawSurfaceChart;
